import { supabase } from '../lib/supabase'
import type { Hotel } from '../hooks/useHotels'

export interface CreateHotelData {
  name_en: string
  name_ar: string
  name_ur: string
  name_fa: string
  city_en: string
  city_ar: string
  city_ur: string
  city_fa: string
  address_en: string
  address_ar: string
  address_ur: string
  address_fa: string
  latitude?: number | null
  longitude?: number | null
  images?: string[]
  description_en?: string
  description_ar?: string
  description_ur?: string
  description_fa?: string
  phone?: string
  email?: string
  website?: string
  rating?: number | null
}

export interface UpdateHotelData extends Partial<CreateHotelData> {
  id: string
}

export class HotelService {
  // Create a new hotel
  static async createHotel(hotelData: CreateHotelData): Promise<{ success: boolean; data?: Hotel; error?: string }> {
    try {
      // Generate a unique ID for the hotel
      const hotelId = crypto.randomUUID()
      
      const { data, error } = await supabase
        .from('hotels')
        .insert([{ ...hotelData, id: hotelId }])
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedHotel = this.transformSupabaseHotelToHotel(data)
      return { success: true, data: transformedHotel }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create hotel' 
      }
    }
  }

  // Update an existing hotel
  static async updateHotel(hotelData: UpdateHotelData): Promise<{ success: boolean; data?: Hotel; error?: string }> {
    try {
      const { id, ...updateData } = hotelData
      
      const { data, error } = await supabase
        .from('hotels')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedHotel = this.transformSupabaseHotelToHotel(data)
      return { success: true, data: transformedHotel }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update hotel' 
      }
    }
  }

  // Soft delete a hotel
  static async deleteHotel(hotelId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('hotels')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', hotelId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete hotel' 
      }
    }
  }

  // Permanently delete a hotel (hard delete)
  static async permanentlyDeleteHotel(hotelId: string, imageUrls: string[] = []): Promise<{ success: boolean; error?: string }> {
    try {
      // First, delete associated images from storage if any
      if (imageUrls.length > 0) {
        const { deleteImage } = await import('../lib/storage')
        for (const url of imageUrls) {
          if (url) {
            await deleteImage(url).catch(err => {
              console.warn('Failed to delete image:', url, err)
            })
          }
        }
      }

      // Then permanently delete the hotel from database
      const { error } = await supabase
        .from('hotels')
        .delete()
        .eq('id', hotelId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to permanently delete hotel' 
      }
    }
  }

  // Restore a soft-deleted hotel
  static async restoreHotel(hotelId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('hotels')
        .update({ deleted_at: null })
        .eq('id', hotelId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to restore hotel' 
      }
    }
  }

  // Get a single hotel by ID
  static async getHotelById(hotelId: string): Promise<{ success: boolean; data?: Hotel; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('hotels')
        .select('*')
        .eq('id', hotelId)
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedHotel = this.transformSupabaseHotelToHotel(data)
      return { success: true, data: transformedHotel }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch hotel' 
      }
    }
  }

  // Transform Supabase hotel data to our Hotel interface
  private static transformSupabaseHotelToHotel(supabaseHotel: any): Hotel {
    return {
      id: supabaseHotel.id,
      name: {
        en: supabaseHotel.name_en,
        ar: supabaseHotel.name_ar,
        ur: supabaseHotel.name_ur,
        fa: supabaseHotel.name_fa
      },
      city: {
        en: supabaseHotel.city_en,
        ar: supabaseHotel.city_ar,
        ur: supabaseHotel.city_ur,
        fa: supabaseHotel.city_fa
      },
      address: {
        en: supabaseHotel.address_en,
        ar: supabaseHotel.address_ar,
        ur: supabaseHotel.address_ur,
        fa: supabaseHotel.address_fa
      },
      latitude: supabaseHotel.latitude,
      longitude: supabaseHotel.longitude,
      images: supabaseHotel.images || [],
      description: {
        en: supabaseHotel.description_en || '',
        ar: supabaseHotel.description_ar || '',
        ur: supabaseHotel.description_ur || '',
        fa: supabaseHotel.description_fa || ''
      },
      phone: supabaseHotel.phone,
      email: supabaseHotel.email,
      website: supabaseHotel.website,
      rating: supabaseHotel.rating,
      deletedAt: supabaseHotel.deleted_at
    }
  }

  // Transform our Hotel interface to Supabase format
  static transformHotelToSupabase(hotel: Hotel): CreateHotelData {
    return {
      name_en: hotel.name.en,
      name_ar: hotel.name.ar,
      name_ur: hotel.name.ur,
      name_fa: hotel.name.fa,
      city_en: hotel.city.en,
      city_ar: hotel.city.ar,
      city_ur: hotel.city.ur,
      city_fa: hotel.city.fa,
      address_en: hotel.address.en,
      address_ar: hotel.address.ar,
      address_ur: hotel.address.ur,
      address_fa: hotel.address.fa,
      latitude: hotel.latitude,
      longitude: hotel.longitude,
      images: hotel.images || [],
      description_en: hotel.description.en,
      description_ar: hotel.description.ar,
      description_ur: hotel.description.ur,
      description_fa: hotel.description.fa,
      phone: hotel.phone,
      email: hotel.email,
      website: hotel.website,
      rating: hotel.rating
    }
  }
}

